// ============================================================================
// Supabase Realtime replacement for useWebSocketExtensions (PartyKit)
// ============================================================================
//
// API SURFACE: exactly matches useWebSocketExtensions.js so WebSocketContext
// can be swapped in one line. Every caller — 18 of them across Ads Manager
// and its dependencies — gets the same shape with no changes needed.
//
// PRESENCE: derived from extension_profiles table via Realtime postgres_changes.
//   - Initial: SELECT all non-archived profiles
//   - Live: subscribe to UPDATE + INSERT events on extension_profiles
//   - "online" = status='connected' AND last_heartbeat < 90s ago
//   - "hasToken" = health_token_valid (set on heartbeat/rich response)
//
// COMMAND RPC: INSERT into extension_commands → Realtime UPDATE events on
// the inserted row → resolve promise when status transitions to completed
// or failed. Same dedup + timeout logic as the WebSocket version.
//
// NO PARTYKIT CONNECTION. When every caller uses this hook, 99-sync/ can
// be deleted. See docs/plans/2026-04-20-dashboard-realtime-migration.md
// for the full 4-commit rollout plan.
// ============================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import api from '../services/api';

// ─── Timeout selection (preserved from old hook) ──────────────────────────
// Must be >= the extension's own timeout for each command (chrome-extension/
// commands/executor.js) AND the server's EXTENSION_COMMAND_TIMEOUTS_SEC map
// (server/src/routes/extension.js). Shorter values here cause the client to
// give up before the server's expires_at, resulting in misleading "command
// timeout" errors while the command actually still runs to completion.
const TIMEOUT_CONFIG = {
  DEFAULT: 8_000,
  FETCH: 5_000,
  CREATE: 180_000,     // 3 min — matches server create_ad=180
  UPLOAD: 45_000,
  BATCH: 600_000,      // 10 min — matches server create_ads_batch=600
  MULTIPLE: 600_000,   // 10 min — matches server create_multiple_ads=600
  FETCH_KEYWORDS: ['get', 'fetch', 'list', 'accounts', 'pages', 'pixels', 'status'],
  CREATE_KEYWORDS: ['create', 'duplicate', 'push'],
  UPLOAD_KEYWORDS: ['upload', 'image'],
  BATCH_KEYWORDS: ['batch', 'bulk', 'multiple'],
};

function getTimeoutForCommand(t) {
  if (!t) return TIMEOUT_CONFIG.DEFAULT;
  const low = String(t).toLowerCase();
  if (low.includes('multiple') || TIMEOUT_CONFIG.BATCH_KEYWORDS.some(k => low.includes(k))) {
    return low.includes('multiple') ? TIMEOUT_CONFIG.MULTIPLE : TIMEOUT_CONFIG.BATCH;
  }
  if (TIMEOUT_CONFIG.UPLOAD_KEYWORDS.some(k => low.includes(k))) return TIMEOUT_CONFIG.UPLOAD;
  if (TIMEOUT_CONFIG.CREATE_KEYWORDS.some(k => low.includes(k))) return TIMEOUT_CONFIG.CREATE;
  if (TIMEOUT_CONFIG.FETCH_KEYWORDS.some(k => low.includes(k))) return TIMEOUT_CONFIG.FETCH;
  return TIMEOUT_CONFIG.DEFAULT;
}

// Online threshold: profile's last_heartbeat must be this recent.
const ONLINE_THRESHOLD_MS = 90_000;

function deriveOnline(profile) {
  if (!profile?.last_heartbeat || profile.status !== 'connected') return false;
  const ageMs = Date.now() - new Date(profile.last_heartbeat).getTime();
  return ageMs < ONLINE_THRESHOLD_MS;
}

function rowToExtension(row) {
  return {
    profileId: row.profile_id,
    profileName: row.profile_name || null,
    accountName: row.primary_bm_name || row.fb_user_name || row.profile_name || null,
    fbUserName: row.fb_user_name || null,
    fbUserId: row.fb_user_id || null,
    accountId: row.fb_account_id || null,
    hasToken: !!row.health_token_valid,
    primaryBmId: row.primary_bm_id || null,
    primaryBmName: row.primary_bm_name || null,
    online: deriveOnline(row),
    connectedAt: row.last_heartbeat ? new Date(row.last_heartbeat).getTime() : Date.now(),
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────

export function useExtensionRealtime() {
  const [extensions, setExtensions] = useState([]);
  const [selectedExtensionId, setSelectedExtensionId] = useState(null);
  const [connected, setConnected] = useState(false);      // realtime WS connected
  const [connecting, setConnecting] = useState(true);
  const [error, setError] = useState(null);
  const channelRef = useRef(null);
  const cmdChannelsRef = useRef(new Map());  // cmdId → channel (for RPC)
  const stalenessIntervalRef = useRef(null);

  // Initial fetch — populate the list synchronously before Realtime kicks in.
  const loadExtensions = useCallback(async () => {
    try {
      if (!supabase) {
        setError('Supabase client not configured');
        return;
      }
      const { data, error: err } = await supabase
        .from('extension_profiles')
        .select('profile_id, profile_name, fb_user_id, fb_user_name, fb_account_id, status, last_heartbeat, health_token_valid, primary_bm_id, primary_bm_name')
        .neq('status', 'archived')
        .order('last_heartbeat', { ascending: false, nullsFirst: false });

      if (err) {
        setError(err.message);
        return;
      }
      setExtensions((data || []).map(rowToExtension));
    } catch (e) {
      setError(e.message);
    }
  }, []);

  // Subscribe to extension_profiles Realtime changes.
  useEffect(() => {
    if (!supabase) {
      setConnecting(false);
      setError('Supabase not configured');
      return;
    }

    loadExtensions();
    setConnecting(true);

    const ch = supabase
      .channel('ext-presence')
      .on('postgres_changes', {
        event: '*',  // INSERT | UPDATE | DELETE
        schema: 'public',
        table: 'extension_profiles',
      }, (payload) => {
        setExtensions((prev) => {
          // DELETE → remove
          if (payload.eventType === 'DELETE') {
            return prev.filter((e) => e.profileId !== payload.old?.profile_id);
          }
          const next = rowToExtension(payload.new);
          // Archived → drop
          if (payload.new?.status === 'archived') {
            return prev.filter((e) => e.profileId !== next.profileId);
          }
          const idx = prev.findIndex((e) => e.profileId === next.profileId);
          if (idx === -1) return [next, ...prev];
          const copy = prev.slice();
          copy[idx] = next;
          return copy;
        });
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setConnected(true);
          setConnecting(false);
          setError(null);
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          setConnected(false);
          if (status === 'CHANNEL_ERROR') setError('Realtime channel error');
        }
      });

    channelRef.current = ch;

    // Staleness ticker: re-evaluate online status every 30s without a DB query.
    // Needed because "online" is time-derived — if a heartbeat stops arriving,
    // the row doesn't UPDATE, so Realtime won't tell us the profile went offline.
    stalenessIntervalRef.current = setInterval(() => {
      setExtensions((prev) => {
        let changed = false;
        const next = prev.map((e) => {
          const wasOnline = e.online;
          const isOnline = e.connectedAt
            ? Date.now() - e.connectedAt < ONLINE_THRESHOLD_MS
            : false;
          if (wasOnline !== isOnline) changed = true;
          return changed ? { ...e, online: isOnline } : e;
        });
        return changed ? next : prev;
      });
    }, 30_000);

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      if (stalenessIntervalRef.current) {
        clearInterval(stalenessIntervalRef.current);
        stalenessIntervalRef.current = null;
      }
      // Clean up any in-flight command subscriptions
      for (const ch of cmdChannelsRef.current.values()) {
        try { supabase.removeChannel(ch); } catch (_) { /* noop */ }
      }
      cmdChannelsRef.current.clear();
    };
  }, [loadExtensions]);

  // Derived lists
  const onlineExtensions = extensions.filter((e) => e.online);
  const offlineExtensions = extensions.filter((e) => !e.online);
  const selectedExtension = extensions.find((e) => e.profileId === selectedExtensionId) || null;

  // Auto-select first online extension if nothing selected
  useEffect(() => {
    if (!selectedExtensionId && onlineExtensions.length > 0) {
      setSelectedExtensionId(onlineExtensions[0].profileId);
    }
  }, [selectedExtensionId, onlineExtensions]);

  const selectExtension = useCallback((profileId) => {
    setSelectedExtensionId(profileId);
  }, []);

  const refreshExtensions = useCallback(() => loadExtensions(), [loadExtensions]);

  // ─── Command RPC via Realtime + extension_commands ──────────────────────

  const sendCommand = useCallback((command, payload = {}, targetProfileId = null, options = {}) => {
    return new Promise(async (resolve, reject) => {
      const profileId = targetProfileId || selectedExtensionId;
      if (!profileId) return reject(new Error('No extension selected'));

      // Extension must be online to execute — match old behavior
      const targetExt = extensions.find((e) => e.profileId === profileId);
      if (!targetExt?.online) return reject(new Error(`Extension ${profileId} is offline`));

      const commandType = typeof command === 'string' ? command : command?.type;
      const timeoutMs = getTimeoutForCommand(commandType);

      // Queue the command via Railway HTTP (preserves existing server-side
      // claim/recover logic + expires_at handling). The server inserts the
      // row with status='pending'; Realtime then delivers it to the extension.
      //
      // expires_in_seconds is explicitly passed so the server's recovery
      // loop respects per-command timeouts. Without it (pre-2026-04-22 builds)
      // long-running commands like create_ads_batch get re-dispatched after 2
      // min, causing duplicate ads.
      let cmdRow;
      try {
        const actionName = typeof command === 'string' ? command : command?.type;
        const expiresInSeconds = Math.ceil(getTimeoutForCommand(actionName) / 1000);
        const body = typeof command === 'string'
          ? { profile_id: profileId, action: command, params: payload, expires_in_seconds: expiresInSeconds }
          : { profile_id: profileId, action: command.type, params: { ...payload, ...command.payload }, expires_in_seconds: expiresInSeconds };
        const resp = await api.post('/extension/commands', body);
        cmdRow = resp?.data?.command || resp?.command || resp;
        if (!cmdRow?.id) {
          return reject(new Error('Failed to queue command — no row id returned'));
        }
        // Notify caller immediately once we have the DB row ID.
        // Caller uses this for localStorage persistence (refresh-recovery).
        if (typeof options.onQueued === 'function') {
          try { options.onQueued(cmdRow.id); } catch (_) {}
        }
      } catch (e) {
        return reject(e);
      }

      // Subscribe to UPDATE events on this specific command row.
      let settled = false;
      const cleanup = () => {
        if (settled) return;
        settled = true;
        const ch = cmdChannelsRef.current.get(cmdRow.id);
        if (ch) {
          try { supabase.removeChannel(ch); } catch (_) {}
          cmdChannelsRef.current.delete(cmdRow.id);
        }
        clearTimeout(timeoutHandle);
      };

      const timeoutHandle = setTimeout(() => {
        cleanup();
        reject(new Error(`Command timeout (${timeoutMs / 1000}s) — extension may be offline or slow`));
      }, timeoutMs);

      const ch = supabase
        .channel(`cmd-${cmdRow.id}`)
        .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: 'extension_commands',
          filter: `id=eq.${cmdRow.id}`,
        }, ({ new: row }) => {
          if (row.status === 'completed') {
            cleanup();
            resolve(row.result);
          } else if (row.status === 'failed' || row.status === 'expired') {
            cleanup();
            reject(new Error(row.error || `Command ${row.status}`));
          }
        })
        .subscribe();

      cmdChannelsRef.current.set(cmdRow.id, ch);
    });
  }, [extensions, selectedExtensionId]);

  // ─── Compatibility shims for the old WS hook's convenience methods ──────

  const getStatus = useCallback((profileId) => sendCommand('ping', {}, profileId), [sendCommand]);
  const listCampaigns = useCallback((accountId, profileId) =>
    sendCommand('get_campaigns', { account_id: accountId }, profileId), [sendCommand]);
  const listAdSets = useCallback((campaignId, profileId) =>
    sendCommand('get_adsets', { campaign_id: campaignId }, profileId), [sendCommand]);
  const loadAccountData = useCallback((accountId, profileId) =>
    sendCommand('get_ad_account', { account_id: accountId }, profileId), [sendCommand]);
  const createAd = useCallback((params, profileId) =>
    sendCommand('create_ad', params, profileId), [sendCommand]);
  const createMultipleAds = useCallback((params, profileId) =>
    sendCommand('create_multiple_ads', params, profileId), [sendCommand]);
  const createCampaign = useCallback((params, profileId) =>
    sendCommand('create_campaign', params, profileId), [sendCommand]);
  const createAdSet = useCallback((params, profileId) =>
    sendCommand('create_adset', params, profileId), [sendCommand]);

  // No-op shims for connect/disconnect/ping (Realtime manages its own lifecycle)
  const connect = useCallback(() => {}, []);
  const disconnect = useCallback(() => {}, []);
  const ping = useCallback(() => {}, []);

  return {
    // Connection state
    connected,
    connecting,
    error,

    // Extensions
    extensions,
    onlineExtensions,
    offlineExtensions,
    selectedExtension,
    selectedExtensionId,
    selectExtension,

    // Commands
    sendCommand,
    getStatus,
    listCampaigns,
    listAdSets,
    loadAccountData,
    createAd,
    createMultipleAds,
    createCampaign,
    createAdSet,

    // Control (no-ops under Realtime)
    connect,
    disconnect,
    ping,
    refreshExtensions,
  };
}

export default useExtensionRealtime;
